import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText, Collapse, Chip, Snackbar, Alert, IconButton,
} from '@mui/material';
import { ExpandLess, ExpandMore, Chat, Close } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useTheme } from '../contexts/ThemeContext';
import { getActiveBackgroundColor, getHoverColor } from '../utils/colorUtils';
import DynamicIcon from './DynamicIcon';
import DynamicDropdownIcon from './DynamicDropdownIcon';
import logouticon from "../assets/logouticon.svg";
import { useDispatch } from "react-redux";
import { logout } from "../Redux/Slices/AuthSlice";
// icons...
import dashboardicon from '../assets/dashboardicon.svg';
import activedashboard from "../assets/activedashboard.svg";
import sidebaricon1 from '../assets/sidebaricon1.svg';
import activenotifications from "../assets/activenotifications.svg";
import activereports from "../assets/activereports.svg";
import activesetting from "../assets/activesettings.svg";
import worker from "../assets/Sidebar/worker.svg";
import attendance from "../assets/Sidebar/attendance.svg";
import task from "../assets/Sidebar/task.svg";
import expence from "../assets/Sidebar/expence.svg";
import document from "../assets/Sidebar/document.svg";
import message from "../assets/Sidebar/message.svg";
import request from "../assets/Sidebar/request.svg";
import reports from "../assets/Sidebar/reports.svg";
import sidebaricon7 from '../assets/sidebaricon7.svg';
import sidebaricon8 from '../assets/sidebaricon8.svg';
import active from '../assets/active.svg';
import websitelogo from "../assets/websitelogo.png";
import projectlogo from "../assets/projectlogo.png";
import activerequest from '../assets/activerequest.svg';
import LogoutModal from './LogoutModal';
import tickets from "../assets/tickets.svg";
import EventBusyIcon from "@mui/icons-material/EventBusy";        // for Absences
import LocalShippingIcon from "@mui/icons-material/LocalShipping"; // for Suppliers
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";     // for Vacancies
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import absent from "../assets/Sidebar/absent.svg";
import suppliers from "../assets/Sidebar/suppliers.svg";
import applicants from "../assets/Sidebar/applicants.svg";
import vacancies from "../assets/Sidebar/vacancies.svg";
import { decryptToken } from "../Utlis/Encryption";
import finance from '../assets/Sidebar/finance.svg';
import loan from "../assets/Sidebar/loan.svg";
import lenders from "../assets/Sidebar/lenders.svg"
import { BASE_URL } from "./BaseUrl";
import {
  isMenuRestrictedForPlan,
} from "../utils/planMenuAccess";

const MESSAGE_TOAST_STORAGE_KEY = "company_admin_message_toast_state";

const Sidebar = ({ mobile = false, bannerOffset = 0, onShowUpgradeModal, isUpgradeModalOpen, subscriptionAccessBlocked = false }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const trialInfo = useSelector((state) => state.auth.trialInfo);
  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;
  useEffect(() => {
    console.log("🟢 Trial Info from Redux:", trialInfo);
    console.log("🟢 Current Plan ID:", trialInfo?.current_plan?.plan_id);
    console.log("🟢 Subscription Status:", trialInfo?.subscription_status);
  }, [trialInfo]);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const dispatch = useDispatch();
  const selectedPath = location.pathname;
  const { primaryColor, secondaryColor, primaryTextColor, secondaryTextColor, companyLogo } = useTheme();
  const [planId, setPlanId] = useState(null);

  useEffect(() => {
    if (!decryptedToken) return;

    async function fetchPlan() {
      try {
        const res = await fetch(`${BASE_URL}/payments/company-admin/plan-details`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${decryptedToken}`,
          },
        });

        const json = await res.json();
        setPlanId(json?.data?.current_plan?.plan_id || null);
      } catch (err) {
        console.error("Error fetching plan details:", err);
      }
    }

    fetchPlan();
  }, [decryptedToken]);

  // Get message counts from Redux state (totalCount = global unread from API/socket)
  const {
    totalCount,
    threadCounts,
    accountExecutiveCount,
    superAdminCount,
    inboxCount
  } = useSelector((state) => state.messageCount || {
    totalCount: 0,
    threadCounts: {},
    accountExecutiveCount: 0,
    superAdminCount: 0,
    inboxCount: 0
  });

  // Sidebar Messages badge: use totalCount (global unread); fallback to sum of role counts
  const totalMessageCount = (typeof totalCount === 'number' && totalCount > 0)
    ? totalCount
    : accountExecutiveCount + superAdminCount + inboxCount;
  const [showMessageToast, setShowMessageToast] = useState(false);
  const [toastUnreadCount, setToastUnreadCount] = useState(0);
  const previousUnreadRef = useRef(null);

  // ✅ Give every parent/child a stable key (NOT translated label)
  const menuItems = useMemo(() => [
    { key: 'dashboard', label: t('sidebar.dashboard'), icon: dashboardicon, activeIcon: activedashboard, path: '/dashboard' },

    {
      key: 'workers', label: t('sidebar.workers'), icon: worker,
      children: [
        { key: 'workers-all', label: t('sidebar.allWorkers'), path: '/all-employees' },
        { key: 'workers-departments', label: t('sidebar.departmentsTeams'), path: '/department' },
      ],
    },

    {
      key: 'attendance', label: t('sidebar.attendance'), icon: attendance,
      children: [
        { key: 'att-today', label: t('todays_punches'), path: '/todays-logs' },
        { key: 'att-unvalidated', label: t('sidebar.unvalidatedPunches'), path: '/unvalidated-punches' },
        { key: 'att-history', label: t('sidebar.attendanceHistory'), path: '/attendance-history' },
        { key: 'att-settings', label: t('sidebar.attendanceSettings'), path: '/attendance-settings' },
      ],
    },
    {
      key: 'tasks', label: t('sidebar.taskManagement'), icon: task,
      children: [
        { key: 'tasks-all', label: t('sidebar.allTasks'), path: '/all-tasks' },
        { key: 'tasks-attendance-history', label: t('sidebar.taskAttendanceHistory'), path: '/task-attendance-history' },
        // { key: 'tasks-projects', label: t('sidebar.calendarView'), path: '/task-calender' },

      ],
    },
    {
      key: 'messages', label: t('sidebar.messages'), icon: message, path: '/messages',

    },
    { key: 'notifications', label: t('sidebar.notifications'), icon: sidebaricon7, activeIcon: activenotifications, path: '/notifications' },
    {
      key: 'requests', label: t('sidebar.requestManagement'), icon: request, activeIcon: activerequest,
      path: '/request-management'
    },
    {
      key: 'documents', label: t('sidebar.document'), icon: document,
      children: [
        { key: 'doc-worker', label: t('sidebar.receivedDocuments'), path: '/worker-documents' },
        { key: 'doc-policies', label: t('sidebar.companiesDocs'), path: '/companies-policies' },
        //  { key: 'auto-docs', label: t('sidebar.autoDocs'), path: '/automated-documents' },
      ],
    },

    {
      key: 'expenses', label: t("payments"), icon: expence,
      children: [
        { key: 'exp-salary', label: 'Reimbursement Request', path: '/Reimbursement-request' },
        { key: 'exp-expense', label: t('remuneration'), path: '/payroll' },

      ],
    },
    { key: 'reports', label: t('sidebar.reports'), icon: reports, activeIcon: activereports, path: '/reports' },




    //         {
    //   key: 'tickets',
    //   label: t('ticket.management'),
    //   icon: tickets,
    //   activeIcon: tickets,
    //   path: '/ticket-management',
    // },

    // {
    //   key: 'messages', label: t('sidebar.messages'), icon: message,
    //   children: [
    //     { key: 'msg-inbox', label: t('sidebar.inbox'), path: '/messages' },
    //     // { key: 'msg-support', label: t('sidebar.support'), path: '/support' },
    //   ],
    // },



    { key: 'absences', label: t('sidebar.absences'), icon: absent, activeIcon: absent, path: '/absence' },
    { key: 'suppliers', label: t('sidebar.suppliers'), icon: finance, activeIcon: finance, path: '/suppliers' },

    { key: 'vacancies', label: t('sidebar.vacancies'), icon: loan, activeIcon: loan, path: '/vacancies' },
    { key: 'applicants', label: t('sidebar.applicants'), icon: lenders, activeIcon: lenders, path: '/applicants' },

    // { key: 'reports-services', label: t('sidebar.reportStatistics'), icon: reports, activeIcon: activereports, path: '/reports-services' },
    { key: 'settings', label: t('sidebar.accountSettings'), icon: sidebaricon8, activeIcon: activesetting, path: '/settings' },
    { key: 'logout', label: t('sidebar.logout'), icon: logouticon, activeIcon: logouticon, path: '' },

  ], [t]);

  const effectivePlanId = planId ?? trialInfo?.current_plan?.plan_id ?? null;

  const isRestrictedMenu = (menuKey) => {
    if (subscriptionAccessBlocked && menuKey !== "logout") return true;
    return isMenuRestrictedForPlan(effectivePlanId, menuKey);
  };

  const logoToShow = (companyLogo && companyLogo.trim() !== '') ? companyLogo : (i18n.language === "es" ? websitelogo : projectlogo);

  // ✅ Only one open at a time
  // Debug theme colors
  console.log("Theme Colors:", {
    primaryColor,
    secondaryColor,
    primaryTextColor,
    secondaryTextColor
  });
  const [openKey, setOpenKey] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [hoveredChild, setHoveredChild] = useState(null);

  const toggleMenu = (key) => {
    setOpenKey(prev => (prev === key ? null : key));
  };



  useEffect(() => {
    const parent = menuItems.find(mi => mi.children?.some(c => c.path === selectedPath));
    setOpenKey(parent ? parent.key : null);
  }, [selectedPath, menuItems]);

  useEffect(() => {
    try {
      const savedToastState = sessionStorage.getItem(MESSAGE_TOAST_STORAGE_KEY);
      if (!savedToastState) return;

      const parsedToastState = JSON.parse(savedToastState);
      if (parsedToastState?.open && parsedToastState?.count > 0) {
        setToastUnreadCount(parsedToastState.count);
        setShowMessageToast(true);
      }
    } catch (error) {
      console.warn("Failed to restore message toast state:", error);
    }
  }, []);

  useEffect(() => {
    const isMessagesPage = location.pathname.includes("/messages");

    if (isMessagesPage) {
      setShowMessageToast(false);
      setToastUnreadCount(0);
      sessionStorage.removeItem(MESSAGE_TOAST_STORAGE_KEY);
      previousUnreadRef.current = totalMessageCount;
      return;
    }

    if (previousUnreadRef.current === null) {
      previousUnreadRef.current = totalMessageCount;
      return;
    }

    if (totalMessageCount > previousUnreadRef.current) {
      setToastUnreadCount(totalMessageCount);
      setShowMessageToast(true);
    }

    previousUnreadRef.current = totalMessageCount;
  }, [totalMessageCount, location.pathname]);

  useEffect(() => {
    if (!showMessageToast || toastUnreadCount <= 0) {
      sessionStorage.removeItem(MESSAGE_TOAST_STORAGE_KEY);
      return;
    }

    sessionStorage.setItem(
      MESSAGE_TOAST_STORAGE_KEY,
      JSON.stringify({ open: true, count: toastUnreadCount })
    );
  }, [showMessageToast, toastUnreadCount]);

  const handleMessageToastClose = (event, reason) => {
    if (reason === "clickaway") return;
    setShowMessageToast(false);
    setToastUnreadCount(0);
  };

  const handleMessageToastNavigate = () => {
    setShowMessageToast(false);
    navigate("/messages");
  };

  const toastMessageText =
    i18n.language === "es"
      ? `${toastUnreadCount} ${toastUnreadCount > 1 ? "mensajes nuevos recibidos" : "mensaje nuevo recibido"}`
      : `${toastUnreadCount} new message${toastUnreadCount > 1 ? "s" : ""} received`;

  return (



    <>
      <Drawer
        variant="permanent"
        sx={{
          width: mobile ? 260 : 210,
          flexShrink: 0,
          // ✅ override BOTH root and paper layers
          '&.MuiDrawer-root': {
            top: `${bannerOffset}px !important`,
            height: `calc(100% - ${bannerOffset}px) !important`,
            position: 'fixed !important',
            zIndex: 1200,
            transition: 'top 0.3s ease',
          },
          '& .MuiDrawer-paper': {
            width: 230,
            boxSizing: 'border-box',
            backgroundColor: 'white',
            px: 0,
            // borderRight: '1px solid #ddd',
            borderRight: 'none',
            boxShadow: 'none',
            display: 'flex',
            flexDirection: 'column',
            pl: 1,
            position: 'fixed !important',
            top: `${bannerOffset}px !important`,
            height: `calc(100% - ${bannerOffset}px) !important`,
            transition: 'top 0.3s ease, height 0.3s ease',
            zIndex: 1200,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', pl: 1, gap: 2 }}>
          <img
            crossOrigin="anonymous"
            src={logoToShow}
            style={{
              width: 180,
              height: 70,
              objectFit: 'contain'
            }}
            alt="Company Logo"
            onLoad={(e) => {

            }}
            onError={(e) => {

              // Fallback to static logo if company logo fails to load
              e.currentTarget.src = i18n.language === "es" ? websitelogo : projectlogo;
            }}
          />
        </Box>

        <Box sx={{ flexGrow: 1, overflowY: 'auto', '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}>
          <List sx={{ py: 1 }}>
            {menuItems.map(item => {
              const isChildActive = !!item.children?.some(c => c.path === selectedPath);
              const isSelfActive = !!item.path && item.path === selectedPath;
              const isOpen = openKey === item.key;

              return (
                <Box
                  key={item.key}
                  sx={{
                    backgroundColor: isChildActive ? getActiveBackgroundColor(primaryColor, 0.15) : isSelfActive ? primaryColor : 'transparent',
                    borderRadius: '6px',
                    mb: 1,
                  }}
                >


                  <ListItemButton


                    onClick={() => {
                      if (item.key === "logout") {
                        setLogoutModalOpen(true);
                        return;
                      }

                      const restricted = isRestrictedMenu(item.key);

                      if (restricted) {
                        if (subscriptionAccessBlocked) {
                          onShowUpgradeModal?.(true);
                          return;
                        }

                        if (item.children) {
                          setOpenKey(item.key);
                          const firstChild = item.children[0];
                          if (firstChild) navigate(firstChild.path);
                        } else if (item.path) {
                          navigate(item.path);
                        }
                        onShowUpgradeModal?.(true);
                        return;
                      }

                      if (isUpgradeModalOpen) {
                        onShowUpgradeModal?.(false);
                      }

                      if (item.children) {
                        const isOpening = openKey !== item.key;
                        setOpenKey(isOpening ? item.key : null);

                        if (isOpening) {
                          const firstChild = item.children[0];
                          if (firstChild && selectedPath !== firstChild.path) {
                            navigate(firstChild.path);
                          }
                        }
                        return;
                      }

                      if (item.path) {
                        navigate(item.path);
                      }
                    }}



                    onMouseEnter={() => setHoveredItem(item.key)}
                    onMouseLeave={() => setHoveredItem(null)}
                    sx={{
                      position: 'relative',
                      gap: 1,
                      px: 2,
                      py: 1,
                      borderRadius: item.children ? '8px 8px 0 0' : '8px',
                      '&:hover': {
                        backgroundColor: secondaryColor,
                        '& .MuiListItemText-primary': {
                          color: secondaryTextColor,
                        },
                        '& .MuiListItemIcon-root': {
                          '& img': {
                            filter: secondaryTextColor === '#FFFFFF' ? 'brightness(0) invert(1)' : 'brightness(0) invert(0)',
                          }
                        },
                        '& svg': {
                          color: secondaryTextColor,
                          fill: secondaryTextColor,
                        },
                        '& .MuiSvgIcon-root': {
                          color: secondaryTextColor,
                          fill: secondaryTextColor,
                        }
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 25, mr: 0.1 }}>
                      <DynamicIcon
                        src={item.icon}
                        activeSrc={item.activeIcon}
                        isActive={isSelfActive}
                        textColor={isSelfActive ? primaryTextColor : '#44546F'}
                        size={25}
                        alt={item.label}
                        isHovered={hoveredItem === item.key}
                        hoverTextColor={secondaryTextColor}
                      />
                    </ListItemIcon>


                    {/* <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: '14px',
                      color: isSelfActive ? primaryTextColor : '#44546F',
                      fontWeight: 500,
                      fontFamily: 'Poppins, sans-serif',
                    }}
                  /> */}
                    <ListItemText
                      primary={
                        item.label.includes('(coming soon)') ? (
                          <>
                            <span>{item.label.replace(' (coming soon)', '')}</span>
                            <br />
                            <span style={{ fontSize: '12px' }}>
                              (coming soon)
                            </span>
                          </>
                        ) : (
                          item.label
                        )
                      }
                      primaryTypographyProps={{
                        fontSize: '14px',
                        color: isSelfActive ? primaryTextColor : '#44546F',
                        fontWeight: 500,
                        fontFamily: 'Poppins, sans-serif',
                      }}
                    />

                    {item.key === 'messages' && totalMessageCount > 0 && (
                      <Chip
                        label={totalMessageCount}
                        size="small"
                        sx={{
                          backgroundColor: secondaryColor,
                          color: secondaryTextColor,
                          fontSize: '10px',
                          height: '18px',
                          minWidth: '18px',
                          '& .MuiChip-label': {
                            px: 0.5,
                            py: 0,
                          },
                        }}
                      />
                    )}

                    {item.children && (
                      <DynamicDropdownIcon
                        isOpen={isOpen}
                        textColor={isSelfActive ? primaryTextColor : '#44546F'}
                        size={20}
                        isHovered={hoveredItem === item.key}
                        hoverTextColor={secondaryTextColor}
                      />
                    )}

                    {isSelfActive && !item.children && (
                      <Box sx={{ position: 'absolute', left: 5, top: 13, width: 3, height: 23, borderRadius: 3, backgroundColor: primaryTextColor }} />
                    )}
                  </ListItemButton>

                  {item.children && (
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {item.children.map(child => {
                          const isChildSelected = child.path === selectedPath;
                          return (
                            <ListItemButton
                              key={child.key}
                              // onClick={() => navigate(child.path)}
                              onClick={() => {
                                if (isRestrictedMenu(item.key)) {
                                  if (subscriptionAccessBlocked) {
                                    onShowUpgradeModal?.(true);
                                    return;
                                  }

                                  navigate(child.path);
                                  onShowUpgradeModal?.(true);
                                  return;
                                }

                                if (isUpgradeModalOpen) {
                                  onShowUpgradeModal?.(false);
                                }

                                navigate(child.path);
                              }}
                              onMouseEnter={() => setHoveredChild(child.key)}
                              onMouseLeave={() => setHoveredChild(null)}
                              sx={{
                                position: 'relative',
                                pl: 3,
                                py: 0.7,
                                gap: 1,
                                backgroundColor: isChildSelected ? primaryColor : 'transparent',
                                borderRadius: '0px 6px 6px 0px',
                                '&:hover': {
                                  backgroundColor: secondaryColor,
                                  '& .MuiListItemText-primary': {
                                    color: secondaryTextColor,
                                  },
                                  '& img': {
                                    filter: secondaryTextColor === '#FFFFFF' ? 'brightness(0) invert(1)' : 'brightness(0) invert(0)',
                                  }
                                },
                              }}
                            >
                              {isChildSelected && (
                                <Box sx={{ position: 'absolute', left: 0, top: 12, width: 3, height: 23, backgroundColor: primaryTextColor, borderRadius: 2 }} />
                              )}
                              <Box sx={{ ml: 1 }}>
                                <DynamicIcon
                                  src={sidebaricon1}
                                  activeSrc={active}
                                  isActive={isChildSelected}
                                  textColor={isChildSelected ? primaryTextColor : '#44546F'}
                                  size={10}
                                  alt="submenu icon"
                                  isHovered={hoveredChild === child.key}
                                  hoverTextColor={secondaryTextColor}
                                />
                              </Box>
                              <ListItemText
                                primary={child.label}
                                primaryTypographyProps={{
                                  fontSize: '14px',
                                  fontWeight: 400,
                                  color: isChildSelected ? primaryTextColor : '#44546F',
                                  fontFamily: 'Poppins, sans-serif',
                                }}
                              />
                              {child.key === 'msg-account-executive' && accountExecutiveCount > 0 && (
                                <Chip
                                  label={accountExecutiveCount}
                                  size="small"
                                  sx={{
                                    backgroundColor: secondaryColor,
                                    color: secondaryTextColor,
                                    fontSize: '10px',
                                    height: '18px',
                                    minWidth: '18px',
                                    '& .MuiChip-label': {
                                      px: 0.5,
                                      py: 0,
                                    },
                                  }}
                                />
                              )}
                              {child.key === 'msg-super-admin' && superAdminCount > 0 && (
                                <Chip
                                  label={superAdminCount}
                                  size="small"
                                  sx={{
                                    backgroundColor: secondaryColor,
                                    color: secondaryTextColor,
                                    fontSize: '10px',
                                    height: '18px',
                                    minWidth: '18px',
                                    '& .MuiChip-label': {
                                      px: 0.5,
                                      py: 0,
                                    },
                                  }}
                                />
                              )}
                              {child.key === 'msg-inbox' && inboxCount > 0 && (
                                <Chip
                                  label={inboxCount}
                                  size="small"
                                  sx={{
                                    backgroundColor: secondaryColor,
                                    color: secondaryTextColor,
                                    fontSize: '10px',
                                    height: '18px',
                                    minWidth: '18px',
                                    '& .MuiChip-label': {
                                      px: 0.5,
                                      py: 0,
                                    },
                                  }}
                                />
                              )}
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </Collapse>
                  )}
                </Box>
              );
            })}
          </List>
        </Box>

      </Drawer>
      <LogoutModal
        open={logoutModalOpen}
        loading={logoutLoading}
        onClose={() => setLogoutModalOpen(false)}
        onConfirm={async () => {
          try {
            setLogoutLoading(true);
            await dispatch(logout());
            navigate("/"); // redirect to login
          } finally {
            setLogoutLoading(false);
            setLogoutModalOpen(false);
          }
        }}
      />
      <Snackbar
        open={showMessageToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        onClose={handleMessageToastClose}
      >
        <Alert
          severity="info"
          variant="filled"
          icon={<Chat fontSize="small" />}
          onClick={handleMessageToastNavigate}
          sx={{
            cursor: "pointer",
            alignItems: "center",
            ".MuiAlert-message": { py: 0.5 },
          }}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={(event) => {
                event.stopPropagation();
                handleMessageToastClose();
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          }
        >
          {toastMessageText}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Sidebar;






